import { ArrowRight, Menu, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { BRAND, type Img } from './brand.config';

/*
 * Primitivas visuais da landing, independentes de marca: tudo que é cor, texto, imagem e rota
 * vem de BRAND (brand.config.tsx). Ao portar para outro app, este arquivo é copiado sem edição.
 */

export const wrap = 'mx-auto w-full max-w-[1200px] px-5 sm:px-8';

/* ---------- Revelação ao rolar ---------- */

/** Fade + 16px ao entrar na tela. Sem IntersectionObserver (ou com movimento reduzido), já aparece. */
export function Reveal({
  as: Tag = 'div',
  delay = 0,
  className,
  children,
}: {
  as?: ElementType;
  /** índice para escalonar irmãos (120ms por passo) */
  delay?: number;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref} data-seen={seen} style={{ '--d': delay } as CSSProperties} className={cn('lp-reveal', className)}>
      {children}
    </Tag>
  );
}

/* ---------- Botões ---------- */

const btn = 'lp-btn inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap px-6 text-[0.9375rem] font-semibold';

export function ButtonLink({
  to,
  kind = 'primary',
  arrow,
  className,
  children,
}: {
  to: string;
  kind?: 'primary' | 'ghost' | 'onBand' | 'ghostOnBand' | 'gold';
  arrow?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cn(btn, `lp-btn-${kind}`, className)}>
      {children}
      {arrow && <ArrowRight className="size-5" strokeWidth={2.5} aria-hidden />}
    </Link>
  );
}

export function Brand({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2.5 font-display text-xl tracking-tight', className)}>
      <img src={BRAND.logo.src} width={BRAND.logo.width} height={BRAND.logo.height} alt={BRAND.logo.alt} className="h-8 w-auto sm:h-9" />
    </span>
  );
}

/* ---------- Foto com plano B ---------- */

/**
 * Imagem WebP com dimensões reservadas. Se o arquivo falhar, entra um painel da marca
 * (sem texto quebrado nem salto de layout). `eager` só para a imagem do hero.
 */
export function Photo({
  img,
  eager,
  className,
  imgClassName,
  style,
  sizes,
  focus,
}: {
  img: Img;
  eager?: boolean;
  /** object-position da foto (ponto focal ao recortar) */
  focus?: string;
  className?: string;
  imgClassName?: string;
  style?: CSSProperties;
  sizes?: string;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={cn('lp-photo relative overflow-hidden', className)} style={{ aspectRatio: `${img.width} / ${img.height}`, ...style }}>
      {failed ? (
        <div role="img" aria-label={img.alt} className="absolute inset-0" />
      ) : (
        <img
          src={img.src}
          sizes={sizes}
          width={img.width}
          height={img.height}
          alt={img.alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding={eager ? 'sync' : 'async'}
          fetchPriority={eager ? 'high' : 'auto'}
          style={focus ? { objectPosition: focus } : undefined}
          onError={() => setFailed(true)}
          className={cn('size-full object-cover', imgClassName)}
        />
      )}
    </div>
  );
}

/* ---------- Título de seção ---------- */

export function SectionHead({ id, title, intro, className }: { id: string; title: string; intro?: string; className?: string }) {
  return (
    <div className={cn('max-w-3xl', className)}>
      <h2 id={id} className="lp-h2 font-display">
        {title}
      </h2>
      {intro && <p className="lp-lead mt-5 max-w-[40rem]">{intro}</p>}
    </div>
  );
}

/* ---------- Cabeçalho ---------- */

export function SiteHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="lp-header sticky top-0 z-40">
      <div className={cn(wrap, 'flex h-[4.5rem] items-center justify-between gap-4')}>
        <Link to={BRAND.routes.home} className="lp-link rounded-xl" aria-label={BRAND.homeLabel}>
          <Brand />
        </Link>

        <nav aria-label="Principal" className="hidden items-center gap-1 lg:flex">
          {BRAND.nav.map((n) => (
            <a key={n.href} href={n.href} className="lp-navlink inline-flex min-h-11 items-center rounded-xl px-3.5 text-sm font-medium">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to={BRAND.routes.login} className="lp-navlink hidden min-h-11 items-center rounded-xl px-3.5 text-sm font-semibold sm:inline-flex">
            {BRAND.cta.login}
          </Link>
          <ButtonLink to={BRAND.routes.signup} className="hidden !min-h-11 !px-5 text-sm sm:inline-flex">
            {BRAND.cta.signup}
          </ButtonLink>
          <button
            type="button"
            className="lp-iconbtn inline-flex size-11 items-center justify-center rounded-xl lg:hidden"
            aria-expanded={open}
            aria-controls="lp-menu"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" aria-hidden /> : <Menu className="size-5" aria-hidden />}
          </button>
        </div>
      </div>

      {/* disclosure simples, no fluxo da página: sem modal */}
      <div id="lp-menu" hidden={!open} className="lp-menu lg:hidden">
        <nav aria-label="Principal (menu)" className={cn(wrap, 'flex flex-col py-2')}>
          {BRAND.nav.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="lp-navlink flex min-h-12 items-center rounded-xl px-2 text-base font-medium">
              {n.label}
            </a>
          ))}
          <div className="lp-menu-actions flex flex-col gap-2 py-4 sm:hidden">
            <ButtonLink to={BRAND.routes.signup}>{BRAND.cta.signup}</ButtonLink>
            <ButtonLink to={BRAND.routes.login} kind="ghost">
              {BRAND.cta.login}
            </ButtonLink>
          </div>
        </nav>
      </div>
    </header>
  );
}

/* ---------- Hero + faixa de diferenciais ---------- */

type SideChip = { icon: LucideIcon; label: string };
type HeroCfg = typeof BRAND.hero & {
  titleMax?: string;
  /** OPCIONAL: tamanho do título em vw (padrão 6.4) */
  titleVw?: number;
  /** OPCIONAL: título em 3 linhas fixas (antes / destaque / depois), sem quebra no desktop */
  threeLines?: boolean;
  photoWidth?: string;
  variant?: 'split' | 'immersive';
  /** OPCIONAL (só no hero 'split'): cartões brancos empilhados à direita da foto no desktop; linha rolável no mobile */
  sideChips?: readonly SideChip[];
};
const isImmersive = () => (BRAND.hero as HeroCfg).variant === 'immersive';

/** Linha de 4 diferenciais (cartão). Usada pela faixa flutuante (split) e pela base do hero imersivo. */
function StripList({ className }: { className?: string }) {
  return (
    <Reveal>
      <ul className={cn('lp-strip grid grid-cols-2 lg:grid-cols-4', className)}>
        {BRAND.strip.map(({ icon: Icon, a, b }) => (
          <li key={a} className="lp-strip-item flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:gap-4 sm:p-6">
            <Icon className="lp-accent-text size-9 shrink-0 sm:size-10" strokeWidth={1.5} aria-hidden />
            <p className="text-[0.95rem] font-medium leading-snug sm:text-base">
              {a}
              <br />
              {b}
            </p>
          </li>
        ))}
      </ul>
    </Reveal>
  );
}

/**
 * Hero imersivo (BRAND.hero.variant = 'immersive'): foto em tela cheia sob um degradê escuro da esquerda
 * (texto branco com contraste AA), título limitado à metade esquerda, faixa de 4 itens ancorada na base,
 * tudo dentro da primeira tela.
 */
function ImmersiveHero({ h }: { h: HeroCfg }) {
  return (
    <section aria-labelledby="lp-h1" className="lp-imm relative isolate flex min-h-[max(34rem,calc(100svh-4.5rem))] flex-col overflow-hidden">
      <Photo
        img={h.image}
        eager
        focus={h.focus}
        sizes="100vw"
        className="!absolute inset-0 -z-10 size-full"
        style={{ aspectRatio: undefined }}
        imgClassName="size-full object-cover"
      />
      <div aria-hidden className="lp-imm-scrim absolute inset-0 -z-10" />
      <div className={cn(wrap, 'flex flex-1 items-center py-12 sm:py-14')}>
        <div className="max-w-[38rem]">
          <h1
            id="lp-h1"
            className="lp-h1 lp-imm-h1 font-display"
            style={{ fontSize: `clamp(2.5rem, 4.8vw, ${h.titleMax ?? '4.25rem'})` }}
          >
            {h.before} <span className="lp-accent-text">{h.highlight}</span>
            {h.after}
          </h1>
          <p className="lp-imm-sub mt-6 max-w-[32rem] text-[1.1875rem] leading-[1.55]">{h.subtitle}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink to={BRAND.routes.signup} kind="gold" arrow className="!min-h-14 !px-8 !text-base">
              {BRAND.cta.signup}
            </ButtonLink>
            <ButtonLink to={BRAND.routes.login} kind="ghostOnBand">
              {BRAND.cta.login}
            </ButtonLink>
          </div>
        </div>
      </div>
      <div className={cn(wrap, 'pb-5 lg:pb-8')}>
        <StripList />
      </div>
    </section>
  );
}

export function Hero() {
  /* titleMax e photoWidth são opcionais: sem eles vale o comportamento original (até 5.75rem, foto em 62%) */
  const h = BRAND.hero as HeroCfg;
  if (h.variant === 'immersive') return <ImmersiveHero h={h} />;
  return (
    <>
      <section aria-labelledby="lp-h1" className="lp-hero relative lg:flex lg:min-h-[max(34rem,calc(100svh-9rem))] lg:items-center">
        <div className={cn(wrap, 'relative z-10 py-12 sm:py-16 lg:py-14')}>
          <div className="lg:max-w-[48rem]">
            <h1
              id="lp-h1"
              className="lp-h1 font-display"
              style={{
                ...(h.titleMax ? { fontSize: `clamp(2.75rem, ${h.titleVw ?? 6.4}vw, ${h.titleMax})` } : {}),
                ...(h.threeLines ? { maxWidth: 'none' } : {}),
              }}
            >
              {h.threeLines ? (
                <>
                  <span className="block lg:whitespace-nowrap">{h.before}</span>
                  <span className="lp-accent-text block lg:whitespace-nowrap">{h.highlight}</span>
                  <span className="block lg:whitespace-nowrap">{h.after.trim()}</span>
                </>
              ) : (
                <>
                  {h.before} <span className="lp-accent-text">{h.highlight}</span>
                  {h.after}
                </>
              )}
            </h1>
            <p className="lp-lead mt-6 max-w-[32rem]">{h.subtitle}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <ButtonLink to={BRAND.routes.signup} arrow className="!min-h-14 !px-8 !text-base">
                {BRAND.cta.signup}
              </ButtonLink>
              <ButtonLink to={BRAND.routes.login} kind="ghost">
                {BRAND.cta.login}
              </ButtonLink>
            </div>
          </div>
        </div>

        {/* foto: sangra até a borda no desktop, com degradê fundindo ao fundo do texto */}
        <div
          className={cn('lg:absolute lg:inset-y-0 lg:right-0 lg:w-[var(--lp-hero-photo,62%)]', h.sideChips && 'lp-hero-photo-wrap')}
          style={h.photoWidth ? ({ '--lp-hero-photo': h.photoWidth } as CSSProperties) : undefined}
        >
          <Photo
            img={h.image}
            eager
            focus={h.focus}
            sizes="(min-width: 1024px) 62vw, 100vw"
            className={cn('mx-5 rounded-[28px] sm:mx-8 lg:m-0 lg:h-full lg:rounded-none', h.sideChips && 'lp-hero-photo-fade')}
            style={{ aspectRatio: undefined }}
            imgClassName="aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto"
          />
          <div aria-hidden className="lp-hero-fade hidden lg:block" />
          {h.sideChips && (
            <ul className="lp-sidechips">
              {h.sideChips.map(({ icon: Icon, label }) => (
                <li key={label} className="lp-sidechip">
                  <Icon className="lp-accent-text size-7 shrink-0" strokeWidth={2} aria-hidden />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

/** Cartão flutuante sobreposto à base do hero. Grade 2x2 no mobile, linha única no desktop. No hero imersivo a faixa já está dentro do hero. */
export function FeatureStrip() {
  if (isImmersive()) return null;
  return (
    <div className={cn(wrap, 'relative z-10 -mt-8 sm:-mt-10 lg:-mt-14')}>
      <StripList />
    </div>
  );
}

/* ---------- Bento de módulos ---------- */

export function Bento() {
  const m = BRAND.modules;
  return (
    <section id={m.id} aria-labelledby="lp-modulos" className="py-20 lg:py-32">
      <div className={wrap}>
        <Reveal>
          <SectionHead id="lp-modulos" title={m.title} intro={m.intro} />
        </Reveal>
        <ul className="mt-12 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-5">
          {m.items.map(({ icon: Icon, title, text }, i) => (
            <Reveal as="li" key={title} delay={i % 4} className="lp-card flex flex-row gap-4 p-4 sm:flex-col sm:gap-0 sm:p-6">
              <span className="lp-iconchip inline-flex size-11 shrink-0 items-center justify-center sm:mb-5 sm:size-12">
                <Icon className="size-6" strokeWidth={1.75} aria-hidden />
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight sm:text-xl">{title}</h3>
                <p className="lp-muted mt-1.5 text-[0.9375rem] sm:mt-2 sm:text-base">{text}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------- Bloco imagem + texto ---------- */

export function ImageText({
  id,
  title,
  text,
  bullets,
  image,
  side,
  ratio,
  visual,
  alt,
}: {
  id: string;
  title: string;
  text: string;
  bullets?: readonly string[];
  image: Img;
  side: 'left' | 'right';
  ratio?: string;
  /** mockup sobreposto à foto */
  visual?: ReactNode;
  alt?: boolean;
}) {
  const hid = `lp-b-${id}`;
  return (
    <section aria-labelledby={hid} className={cn('py-16 lg:py-28', alt && 'lp-alt')}>
      <div className={cn(wrap, 'grid items-center gap-10 lg:grid-cols-2 lg:gap-20')}>
        <Reveal className={cn('relative', side === 'right' && 'lg:order-2')}>
          <Photo img={image} sizes="(min-width: 1024px) 560px, 100vw" className={cn('lp-photo-lg', ratio)} style={ratio ? { aspectRatio: undefined } : undefined} />
          {visual}
        </Reveal>
        <Reveal delay={1}>
          <h2 id={hid} className="lp-h2 font-display">
            {title}
          </h2>
          <p className="lp-lead mt-5 max-w-[34rem]">{text}</p>
          {bullets && (
            <ul className="mt-6 max-w-[34rem] space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex gap-3">
                  <span aria-hidden className="lp-dot mt-2.5 size-2 shrink-0 rounded-full" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Passos ---------- */

export function Steps() {
  const s = BRAND.steps;
  return (
    <section id={s.id} aria-labelledby="lp-comecar" className="lp-dark py-20 lg:py-32">
      <div className={cn(wrap, 'grid gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20')}>
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <SectionHead id="lp-comecar" title={s.title} intro={s.intro} />
        </Reveal>
        <ol className="space-y-4">
          {s.items.map((it, i) => (
            <Reveal as="li" key={it.title} delay={i % 2} className="lp-card flex items-center gap-5 p-5 sm:gap-7 sm:p-6">
              <span aria-hidden className="tabular lp-accent-text w-14 shrink-0 text-center font-display text-5xl font-extrabold leading-none sm:w-16 sm:text-6xl">
                {i + 1}
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight sm:text-xl">{it.title}</h3>
                <p className="lp-muted mt-1">{it.text}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------- Segurança ---------- */

export function SecurityBlock() {
  const s = BRAND.security;
  return (
    <section id={s.id} aria-labelledby="lp-seguranca" className="lp-alt py-20 lg:py-32">
      <div className={wrap}>
        <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
          <Reveal>
            <SectionHead id="lp-seguranca" title={s.title} intro={s.intro} />
          </Reveal>
          <Reveal delay={1} className="hidden lg:block">
            <Photo img={s.image} sizes="480px" className="lp-photo-lg aspect-[16/10]" style={{ aspectRatio: undefined }} />
          </Reveal>
        </div>

        {/* 7 controles + a nota da LGPD como 8º cartão: fecha 4x2 no desktop e 2x4 no tablet */}
        <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {s.controls.map((c, i) => (
            <Reveal as="li" key={c.title} delay={i % 4} className="lp-card p-6">
              <h3 className="font-display text-lg font-semibold tracking-tight">{c.title}</h3>
              <p className="lp-muted mt-2">{c.text}</p>
            </Reveal>
          ))}
          <Reveal as="li" delay={3} className="lp-caveat p-6">
            <p className="text-base font-medium">{s.caveat}</p>
          </Reveal>
        </ul>
      </div>
    </section>
  );
}

/* ---------- Perguntas frequentes (acordeão) ---------- */

export function FaqAccordion() {
  const f = BRAND.faq;
  return (
    <section id={f.id} aria-labelledby="lp-perguntas" className="py-20 lg:py-32">
      <div className={cn(wrap, 'grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] lg:gap-20')}>
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <h2 id="lp-perguntas" className="lp-h2 font-display">
            {f.title}
          </h2>
        </Reveal>
        <div className="lp-faq space-y-3">
          {f.items.map((it) => (
            <details key={it.q} className="lp-card group">
              <summary className="flex min-h-14 cursor-pointer items-center justify-between gap-6 rounded-[inherit] px-5 py-4 font-display text-lg font-semibold sm:px-6">
                {it.q}
                <Plus className="lp-faq-mark lp-accent-text size-5 shrink-0" strokeWidth={2.5} aria-hidden />
              </summary>
              <p className="lp-muted max-w-[40rem] px-5 pb-6 sm:px-6">{it.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Faixa final de CTA ---------- */

export function FinalCta() {
  const c = BRAND.finalCta;
  return (
    <section aria-labelledby="lp-cta" className={cn(wrap, 'pb-20 lg:pb-28')}>
      <Reveal className="lp-band relative overflow-hidden px-6 py-16 sm:px-12 lg:px-16 lg:py-24">
        {/* foto decorativa de fundo (sem alt): fica em CSS, sob o degradê da marca */}
        <div aria-hidden className="absolute inset-0 bg-cover bg-center opacity-35" style={{ backgroundImage: `url(${c.image})` }} />
        <div aria-hidden className="lp-band-fade absolute inset-0" />
        <div className="relative">
          <h2 id="lp-cta" className="lp-h2 max-w-2xl font-display">
            {c.title}
          </h2>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <ButtonLink to={BRAND.routes.signup} kind="onBand" arrow>
              {BRAND.cta.signup}
            </ButtonLink>
            <ButtonLink to={BRAND.routes.login} kind="ghostOnBand">
              {BRAND.cta.login}
            </ButtonLink>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- Rodapé ---------- */

export function SiteFooter() {
  return (
    <footer className="lp-footer py-12">
      <div className={cn(wrap, 'grid gap-10 md:grid-cols-[minmax(0,1fr)_auto]')}>
        <div className="max-w-md">
          <Brand />
          <p className="lp-muted mt-4 text-sm">{BRAND.footer.note}</p>
        </div>
        <nav aria-label="Rodapé" className="flex flex-col gap-1 text-sm font-medium md:items-end">
          <Link to={BRAND.routes.signup} className="lp-navlink inline-flex min-h-11 items-center rounded-xl px-2">
            {BRAND.cta.signup}
          </Link>
          <Link to={BRAND.routes.login} className="lp-navlink inline-flex min-h-11 items-center rounded-xl px-2">
            {BRAND.cta.login}
          </Link>
          {BRAND.footer.links.map((l) => (
            <a key={l.href} href={l.href} className="lp-navlink lp-muted inline-flex min-h-11 items-center rounded-xl px-2">
              {l.label}
            </a>
          ))}
          {BRAND.footer.legal.map((l) => (
            <Link key={l.to} to={l.to} className="lp-navlink lp-muted inline-flex min-h-11 items-center rounded-xl px-2">
              {l.label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}
